import type { SmsStatus } from '@/lib/api/notifications.api'
import { getCurrentClientPortalContext } from '@/lib/api/client-portal-context'
import { notificationsApi } from '@/lib/api/notifications.api'

import { SessionChangedError } from '@scrambl/shared/session-scope'

import type { NotificationReaderContext } from './reads.api'
import {
  assertNotificationReaderContext,
  NotificationReadContractError,
} from './reads.api'

export type SmsCommand =
  | { kind: 'request'; phoneNumber: string; consentAccepted: boolean }
  | { kind: 'confirm'; phoneNumber: string; code: string }
  | { kind: 'revoke' }
export type SmsCommandAck =
  | { kind: 'request'; userId: string; phoneNumber: string; expiresAt: string }
  | { kind: 'confirm' | 'revoke'; userId: string }
function assertContext(context: NotificationReaderContext) {
  assertNotificationReaderContext(context)
  if (getCurrentClientPortalContext()) throw new SessionChangedError()
}
/** Match the existing server's supported country-code/US input rules. */
export function normalizeSmsPhone(value: unknown) {
  if (
    typeof value !== 'string' ||
    value.length > 80 ||
    !/^[+0-9()\-\s.]+$/.test(value)
  )
    throw new NotificationReadContractError()
  const trimmed = value.trim(),
    digits = trimmed.replace(/\D/g, '')
  const phone = trimmed.startsWith('+')
    ? `+${digits}`
    : digits.length === 10
      ? `+1${digits}`
      : digits.length === 11 && digits.startsWith('1')
        ? `+${digits}`
        : ''
  if (!/^\+[1-9]\d{7,14}$/.test(phone))
    throw new NotificationReadContractError()
  return phone
}
function timestamp(value: unknown) {
  if (
    typeof value !== 'string' ||
    value.length > 64 ||
    !Number.isFinite(Date.parse(value))
  )
    throw new NotificationReadContractError()
  return Date.parse(value)
}
function validateStatus(value: SmsStatus, context: NotificationReaderContext) {
  assertContext(context)
  if (
    !value ||
    value.userId !== context.userId ||
    typeof value.enabled !== 'boolean' ||
    typeof value.disclosureVersion !== 'string' ||
    !value.disclosureVersion ||
    value.disclosureVersion.length > 64
  )
    throw new NotificationReadContractError()
  if (
    value.phoneNumber !== null &&
    normalizeSmsPhone(value.phoneNumber) !== value.phoneNumber
  )
    throw new NotificationReadContractError()
  for (const field of [
    value.phoneVerified,
    value.consentGrantedAt,
    value.consentRevokedAt,
  ])
    if (field !== null) timestamp(field)
  if (
    value.consentVersion !== null &&
    (typeof value.consentVersion !== 'string' ||
      value.consentVersion.length > 64 ||
      !value.consentVersion)
  )
    throw new NotificationReadContractError()
  const active =
    !!value.phoneNumber &&
    !!value.phoneVerified &&
    !!value.consentGrantedAt &&
    (!value.consentRevokedAt ||
      timestamp(value.consentGrantedAt) > timestamp(value.consentRevokedAt))
  if (value.enabled !== active) throw new NotificationReadContractError()
  return value
}
const route = (context: NotificationReaderContext) => ({
  signal: context.signal,
  organizationId: context.organizationId,
})
export const smsSettingsApi = {
  async status(context: NotificationReaderContext) {
    assertContext(context)
    return validateStatus(
      await notificationsApi.getSmsStatus(route(context)),
      context,
    )
  },
  async command(
    input: SmsCommand,
    context: NotificationReaderContext,
  ): Promise<SmsCommandAck> {
    assertContext(context)
    if (input.kind === 'request') {
      if (input.consentAccepted !== true)
        throw new NotificationReadContractError()
      const phoneNumber = normalizeSmsPhone(input.phoneNumber)
      const result = await notificationsApi.requestSmsVerification(
        { phoneNumber, consentAccepted: true },
        route(context),
      )
      assertContext(context)
      if (
        !result ||
        result.userId !== context.userId ||
        result.sent !== true ||
        result.phoneNumber !== phoneNumber ||
        timestamp(result.expiresAt) <= Date.now()
      )
        throw new NotificationReadContractError()
      return {
        kind: 'request',
        userId: result.userId,
        phoneNumber,
        expiresAt: result.expiresAt,
      }
    }
    if (input.kind === 'confirm') {
      const phoneNumber = normalizeSmsPhone(input.phoneNumber)
      if (typeof input.code !== 'string' || !/^\d{6}$/.test(input.code))
        throw new NotificationReadContractError()
      const status = validateStatus(
        await notificationsApi.confirmSmsVerification(
          { phoneNumber, code: input.code },
          route(context),
        ),
        context,
      )
      if (status.phoneNumber !== phoneNumber || !status.enabled)
        throw new NotificationReadContractError()
      return { kind: 'confirm', userId: status.userId }
    }
    if (input.kind !== 'revoke') throw new NotificationReadContractError()
    const status = validateStatus(
      await notificationsApi.revokeSmsConsent(route(context)),
      context,
    )
    if (status.enabled || !status.consentRevokedAt)
      throw new NotificationReadContractError()
    return { kind: 'revoke', userId: status.userId }
  },
}
